import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import { Address, AddressPayload, Country } from '../../../../models/supplierAdministration';

type AddressDialogProps = {
	open: boolean;
	addressToEdit: Address | null;
	countries: Country[];
	onClose: () => void;
	onSubmit: (payload: AddressPayload) => Promise<void>;
}

const AddressDialog = ({ open, addressToEdit, countries, onClose, onSubmit }: AddressDialogProps) => {
	const [form, setForm] = useState({
		name: '',
		address: '',
		postalCode: '',
		city: '',
		idCountry: '',
		email: '',
		phone: '',
		secondPhone: '',
	});
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		setForm({
			name: addressToEdit?.name || '',
			address: addressToEdit?.address || '',
			postalCode: addressToEdit?.postalCode || '',
			city: addressToEdit?.city || '',
			idCountry: addressToEdit?.country?.id ? String(addressToEdit.country.id) : '',
			email: addressToEdit?.email || '',
			phone: addressToEdit?.phone || '',
			secondPhone: addressToEdit?.secondPhone || '',
		});
		setError(null);
	}, [addressToEdit, open]);

	const selectedCountry = useMemo(
		() => countries.find((country) => String(country.id) === form.idCountry) || null,
		[countries, form.idCountry]
	);

	const updateField = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => {
		setForm((current) => ({ ...current, [field]: event.target.value }));
	};

	const validate = () => {
		if (!form.name.trim()) return 'Le nom est obligatoire.';
		if (!form.address.trim()) return "L'adresse est obligatoire.";
		if (!form.postalCode.trim()) return 'Le code postal est obligatoire.';
		if (!form.city.trim()) return 'La ville est obligatoire.';
		return null;
	};

	const optionalString = (value: string) => {
		const trimmed = value.trim();
		return trimmed ? trimmed : undefined;
	};

	const submit = async () => {
		const validationError = validate();
		if (validationError) {
			setError(validationError);
			return;
		}

		setLoading(true);
		setError(null);
		try {
			await onSubmit({
				name: form.name.trim(),
				address: form.address.trim(),
				postalCode: form.postalCode.trim(),
				city: form.city.trim(),
				idCountry: selectedCountry?.id,
				email: optionalString(form.email),
				phone: optionalString(form.phone),
				secondPhone: optionalString(form.secondPhone),
			});
			onClose();
		} catch (error: any) {
			setError(error?.message || 'Une erreur est survenue.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>{addressToEdit ? "Modifier l'adresse" : 'Créer une adresse'}</DialogTitle>
			<DialogContent>
				<Grid container spacing={2} sx={{ mt: 0.5 }}>
					{error && (
						<Grid size={12}>
							<Alert severity="error">{error}</Alert>
						</Grid>
					)}
					<Grid size={{ xs: 12, md: 6 }}>
						<TextField label="Nom" value={form.name} onChange={updateField('name')} fullWidth required inputProps={{ maxLength: 100 }} />
					</Grid>
					<Grid size={{ xs: 12, md: 6 }}>
						<Autocomplete
							options={countries}
							value={selectedCountry}
							onChange={(_, newValue) => setForm((current) => ({ ...current, idCountry: newValue ? String(newValue.id) : '' }))}
							getOptionLabel={(option) => `${option.name} (${option.code})`}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							renderInput={(params) => <TextField {...params} label="Pays" />}
						/>
					</Grid>
					<Grid size={12}>
						<TextField label="Adresse" value={form.address} onChange={updateField('address')} fullWidth required inputProps={{ maxLength: 255 }} />
					</Grid>
					<Grid size={{ xs: 12, md: 4 }}>
						<TextField label="Code postal" value={form.postalCode} onChange={updateField('postalCode')} fullWidth required inputProps={{ maxLength: 20 }} />
					</Grid>
					<Grid size={{ xs: 12, md: 8 }}>
						<TextField label="Ville" value={form.city} onChange={updateField('city')} fullWidth required inputProps={{ maxLength: 100 }} />
					</Grid>
					<Grid size={{ xs: 12, md: 4 }}>
						<TextField label="Email" value={form.email} onChange={updateField('email')} fullWidth inputProps={{ maxLength: 255 }} />
					</Grid>
					<Grid size={{ xs: 12, md: 4 }}>
						<TextField label="Téléphone" value={form.phone} onChange={updateField('phone')} fullWidth inputProps={{ maxLength: 20 }} />
					</Grid>
					<Grid size={{ xs: 12, md: 4 }}>
						<TextField label="Téléphone secondaire" value={form.secondPhone} onChange={updateField('secondPhone')} fullWidth inputProps={{ maxLength: 20 }} />
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
					Annuler
				</Button>
				<Button variant="contained" onClick={submit} disabled={loading}>
					Enregistrer
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AddressDialog;
